/**
 * User Management Reports
 * Aggregated insights for users, roles, hierarchy and reporting span
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useUserManagement } from '@/hooks/useUserManagement';
import type { User } from '@/services/user-management.service';
import { roleLabel } from '@/utils/roleLabel.util';

const Reports: React.FC = () => {
  const { users, roles, loading: hookLoading, usersLoading, error, loadUsers, loadRoles, getUserReportingTree } =
    useUserManagement();
  const loading = hookLoading || usersLoading;
  const [selectedManager, setSelectedManager] = useState('');
  const [reportingStats, setReportingStats] = useState<{
    totalReports: number;
    directReports: number;
  } | null>(null);
  const [loadingTree, setLoadingTree] = useState(false);

  useEffect(() => {
    loadUsers({ limit: 200 });
    loadRoles();
  }, [loadUsers, loadRoles]);

  useEffect(() => {
    const loadTree = async () => {
      if (!selectedManager) {
        setReportingStats(null);
        return;
      }
      setLoadingTree(true);
      try {
        const tree = await getUserReportingTree(selectedManager);
        setReportingStats({
          totalReports: tree.totalReports || 0,
          directReports: tree.directReports?.length || 0,
        });
      } finally {
        setLoadingTree(false);
      }
    };
    loadTree();
  }, [selectedManager, getUserReportingTree]);

  const activeUsers = useMemo(() => users.filter((user) => user.isActive), [users]);
  const inactiveUsers = users.length - activeUsers.length;
  const usersWithManagers = users.filter((user) => Boolean(user.reportingTo)).length;
  const topLevelUsers = users.length - usersWithManagers;

  const usersByRole = useMemo(() => {
    const map = new Map<string, number>();
    users.forEach((user) => {
      const roleName = user.roleId?.name || user.role;
      map.set(roleName, (map.get(roleName) || 0) + 1);
    });
    return Array.from(map.entries()).sort((a, b) => b[1] - a[1]);
  }, [users]);

  const managers = useMemo(() => {
    const managerIds = new Set(users.filter((u) => u.reportingTo?._id).map((u) => u.reportingTo?._id));
    return users.filter((u) => managerIds.has(u._id));
  }, [users]);

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Total Users', value: users.length },
          { label: 'Active Users', value: activeUsers.length },
          { label: 'Inactive Users', value: inactiveUsers },
          { label: 'Total Roles', value: roles.length },
          { label: 'Users With Managers', value: usersWithManagers },
          { label: 'Top-Level Users', value: topLevelUsers },
        ].map((item) => (
          <Grid item xs={6} md={4} key={item.label}>
            <Card>
              <CardContent>
                <Typography variant="overline" color="text.secondary">
                  {item.label}
                </Typography>
                <Typography variant="h4" fontWeight={700}>
                  {loading ? <CircularProgress size={22} /> : item.value}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={7}>
          <Card>
            <CardContent>
              <Typography variant="h6" mb={2}>
                Role Distribution
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Role</TableCell>
                    <TableCell align="right">Users</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {usersByRole.map(([role, count]) => (
                    <TableRow key={role}>
                      <TableCell>{roleLabel(role)}</TableCell>
                      <TableCell align="right">{count}</TableCell>
                    </TableRow>
                  ))}
                  {usersByRole.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={2} align="center">
                        No user data
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={5}>
          <Card>
            <CardContent>
              <Typography variant="h6" mb={2}>
                Reporting Span
              </Typography>
              <Select
                fullWidth
                size="small"
                value={selectedManager}
                onChange={(e) => setSelectedManager(e.target.value)}
                displayEmpty
              >
                <MenuItem value="">Select manager</MenuItem>
                {managers.map((user: User) => (
                  <MenuItem key={user._id} value={user._id}>
                    {user.firstName} {user.lastName} ({user.email})
                  </MenuItem>
                ))}
              </Select>

              <Box sx={{ mt: 2 }}>
                {loadingTree ? (
                  <CircularProgress size={20} />
                ) : reportingStats ? (
                  <>
                    <Typography variant="body2" color="text.secondary">
                      Direct Reports
                    </Typography>
                    <Typography variant="h5" fontWeight={700} mb={1}>
                      {reportingStats.directReports}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Reports (All Levels)
                    </Typography>
                    <Typography variant="h5" fontWeight={700}>
                      {reportingStats.totalReports}
                    </Typography>
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Select a manager to view reporting span.
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Reports;
